import EventEmitter from 'events'

import {getUUID} from '@shared/helpers/identity'

import {buildLogValueNodes} from '../helpers'

import type {
    AddTraceEventEmitterProps,
    ClearTracesEventEmitterProps,
    TraceEventEmitterCallback,
    TraceEventEmitterProps,
} from './type'

class TraceEventEmitter extends EventEmitter {
    readonly eventName = 'web-console-trace-event'
    private pendingQueue: TraceEventEmitterProps[] = []

    /**
     * WebConsole의 lazy load로 인해 mount시에 호출된 이벤트는 처리되지 않을 수 있습니다.
     * 따라서 이벤트 리스너가 없는 경우에는 이벤트를 큐에 저장하고, 리스너가 추가되면 큐에 저장된 이벤트를 모두 처리합니다.
     */
    private emitOrQueue(props: TraceEventEmitterProps) {
        if (this.listenerCount(this.eventName) === 0) {
            this.pendingQueue.push(props)
        } else {
            this.emit(this.eventName, props)
        }
    }

    addTrace(scope: string, ...messages: unknown[]) {
        const eventEmitterProps: AddTraceEventEmitterProps = {
            id: getUUID(),
            scope,
            message: buildLogValueNodes(messages),
            timestamp: Date.now(),
            action: 'add',
        }

        this.emitOrQueue(eventEmitterProps)
    }

    createScopedTracer(scope: string) {
        return (...messages: unknown[]) => {
            this.addTrace(scope, ...messages)
        }
    }

    clearTraces() {
        const eventEmitterProps: ClearTracesEventEmitterProps = {
            action: 'clear',
        }

        this.emitOrQueue(eventEmitterProps)
    }

    addEventListener(callback: TraceEventEmitterCallback) {
        const listener = this.addListener(this.eventName, callback)
        const pending = this.pendingQueue.splice(0)
        pending.forEach((props) => this.emit(this.eventName, props))
        return listener
    }

    removeEventListener(callback: TraceEventEmitterCallback) {
        return this.removeListener(this.eventName, callback)
    }
}

const traceEventEmitter = new TraceEventEmitter()

export default traceEventEmitter
